import { useState, useEffect } from "react";
import CartOperationManager from "../services/CartOperationManager";

interface CartOperationOptions {
  anyGroup?: boolean; // New option to check if any group has operations
}

/**
 * Hook to check if operations are in progress for a specific group or item
 * @param groupId - Optional group ID to check
 * @param itemId - Optional item ID to check (must provide groupId if using this)
 * @param options - Optional configuration options
 */
export function useCartOperationStatus(
  groupId?: string,
  itemId?: string,
  options: CartOperationOptions = {}
) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [operationType, setOperationType] = useState<string | null>(null);
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  useEffect(() => {
    // Initial check
    const checkStatus = () => {
      // Case 0: Check if any group has operations (new feature)
      if (options.anyGroup) {
        const activeGroupOperations =
          CartOperationManager.getAllGroupOperations();
        const activeGroupIds = Object.keys(activeGroupOperations).filter(
          (gid) =>
            activeGroupOperations[gid].some((op) => op.status === "IN_PROGRESS")
        );

        // If any group has active operations, set loading to true
        const isAnyGroupUpdating = activeGroupIds.length > 0;
        setIsLoading(isAnyGroupUpdating);

        // Set the operation types from active groups
        if (isAnyGroupUpdating) {
          // Find the first active operation type to report
          for (const gid of activeGroupIds) {
            const activeOp = activeGroupOperations[gid].find(
              (op) => op.status === "IN_PROGRESS"
            );
            if (activeOp) {
              setOperationType(activeOp.type || "GROUP_OPERATIONS");
              break;
            }
          }
        } else {
          setOperationType(null);
        }

        // Track which groups are currently active
        setActiveGroups(activeGroupIds);

        return;
      }

      // Case 1: Check for specific item operations (most specific)
      if (itemId && groupId) {
        const operations = CartOperationManager.getOperationsForItem(itemId);
        const isItemBeingUpdated = operations.some(
          (op) => op.status === "IN_PROGRESS"
        );

        if (isItemBeingUpdated) {
          const activeOp = operations.find((op) => op.status === "IN_PROGRESS");
          setIsLoading(true);
          setOperationType(activeOp?.type || null);
          return;
        }
      }

      // Case 2: Check for group operations or global operations affecting this group
      if (groupId) {
        const isBeingUpdated =
          CartOperationManager.isGroupBeingUpdated(groupId);
        setIsLoading(isBeingUpdated);

        // Determine operation type
        const operations = CartOperationManager.getOperationsForGroup(groupId);
        const activeOp = operations.find((op) => op.status === "IN_PROGRESS");
        setOperationType(activeOp?.type || null);
        return;
      }

      // Case 3: Check for any global operations (like location updates)
      const hasLocationUpdate =
        CartOperationManager.hasActiveOperations("LOCATION_UPDATE");
      setIsLoading(hasLocationUpdate);
      setOperationType(hasLocationUpdate ? "LOCATION_UPDATE" : null);
    };

    // Do initial check
    checkStatus();

    // Set up listeners for changes
    const handleOperationChange = () => {
      checkStatus();
    };

    CartOperationManager.on("operation:registered", handleOperationChange);
    CartOperationManager.on("operation:started", handleOperationChange);
    CartOperationManager.on("operation:completed", handleOperationChange);
    CartOperationManager.on("operation:failed", handleOperationChange);

    return () => {
      CartOperationManager.removeListener(
        "operation:registered",
        handleOperationChange
      );
      CartOperationManager.removeListener(
        "operation:started",
        handleOperationChange
      );
      CartOperationManager.removeListener(
        "operation:completed",
        handleOperationChange
      );
      CartOperationManager.removeListener(
        "operation:failed",
        handleOperationChange
      );
    };
  }, [groupId, itemId, options.anyGroup]);

  // Enhanced return value with additional info for anyGroup option
  return options.anyGroup
    ? { isLoading, operationType, activeGroups }
    : { isLoading, operationType };
}
